import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import EmptyState from "@/components/common/EmptyState";
import ResourceDrawer from "@/components/common/ResourceDrawer";
import { Button } from "@/components/ui/button";
import { Plus, Landmark } from "lucide-react";

export default function BankAccounts() {
    const [items, setItems] = useState([]);
    const [drawer, setDrawer] = useState({ open: false, item: null });
    const load = async () => {
        const { data } = await api.get("/bank-accounts");
        setItems(data);
    };
    useEffect(() => { load(); }, []);

    const fields = [
        { name: "name", label: "Account name", span: 2 },
        { name: "account_type", label: "Type", type: "select", options: [{ value: "bank", label: "Bank" }, { value: "cash", label: "Cash" }, { value: "credit_card", label: "Credit Card" }] },
        { name: "currency", label: "Currency", type: "select", options: ["INR", "USD", "EUR", "GBP"].map((c) => ({ value: c, label: c })) },
        { name: "bank_name", label: "Bank name" },
        { name: "account_number", label: "Account number" },
        { name: "ifsc", label: "IFSC code" },
        { name: "opening_balance", label: "Opening balance", type: "number" },
    ];

    const save = async (form) => {
        if (drawer.item) await api.put(`/bank-accounts/${drawer.item.id}`, form);
        else await api.post(`/bank-accounts`, form);
        await load();
    };

    return (
        <>
            <PageHeader kicker="BANKING" title="Bank & Cash Accounts" subtitle="Connected ledgers for every account, in every currency."
                actions={<Button onClick={() => setDrawer({ open: true, item: null })} data-testid="bank-new"><Plus className="w-4 h-4 mr-1.5" />New account</Button>} />
            <PageBody>
                {items.length === 0 ? <EmptyState icon={Landmark} title="No accounts" description="Add a bank or cash account to start." actionLabel="Add account" onAction={() => setDrawer({ open: true, item: null })} /> :
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {items.map((b) => (
                            <button key={b.id} onClick={() => setDrawer({ open: true, item: b })} className="text-left bg-card border border-border rounded-md p-5 hover:border-gold/40 transition-all" data-testid={`bank-card-${b.id}`}>
                                <div className="flex items-center justify-between">
                                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{b.account_type === "cash" ? "CASH" : b.account_type === "credit_card" ? "CREDIT CARD" : "BANK"}</div>
                                    <span className="text-[10px] uppercase tracking-[0.12em] text-gold">{b.currency}</span>
                                </div>
                                <div className="text-[17px] font-semibold mt-2">{b.name}</div>
                                {b.account_number && <div className="text-[12px] text-muted-foreground font-mono mt-0.5">•••• {b.account_number.slice(-4)}</div>}
                                <div className="serif-num text-3xl mt-4">{fmtMoney(b.current_balance, b.currency)}</div>
                                {b.ifsc && <div className="text-[11px] text-muted-foreground mt-3 font-mono">{b.ifsc}</div>}
                            </button>
                        ))}
                    </div>}
            </PageBody>
            <ResourceDrawer open={drawer.open} onClose={() => setDrawer({ open: false, item: null })} title={drawer.item ? "Edit account" : "New account"} fields={fields} initial={drawer.item || { account_type: "bank", currency: "INR", opening_balance: 0 }} onSave={save} />
        </>
    );
}
